'use client';

import type { ProductNoticeItem } from 'src/actions/products/product-bff';
import type {
  ProductCrossChannelMatch,
  ProductRankingDetailResponse,
} from 'src/types/product-rankings/product-rankings-ops';
import type {
  ProductRankingHistoryPoint,
  ProductRankingHistorySummary,
} from 'src/types/product-rankings/product-rankings-domain';

import { useRef, useMemo, useEffect, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { marketKeys } from 'src/query-keys/market';
import { fetchAmazonProductGallery } from 'src/actions/products/product-bff';
import {
  fetchProductCrossChannel,
  fetchProductRankingDetail,
  fetchProductRankingHistory,
} from 'src/actions/els/product-rankings';

import { detailRefFor, hasCollectedDetail } from '../../../utils/market-rankings-product';
import { cacheKey, runCached, invalidateCache } from '../../../utils/market-rankings-request-cache';

import type { MarketProduct } from '../../../utils/market-rankings-product';

export type ProductHistoryState = {
  loading: boolean;
  error: string | null;
  points: ProductRankingHistoryPoint[];
  summary: ProductRankingHistorySummary | null;
};

export type ProductCrossState = {
  loading: boolean;
  error: string | null;
  items: ProductCrossChannelMatch[];
  /** 매칭을 **건너뛴** 이유 — 브랜드를 모르면 서버가 시도조차 안 한다 */
  note: string;
};

export type ProductDetailState = {
  loading: boolean;
  error: string | null;
  data: ProductRankingDetailResponse | null;
};

const EMPTY_HISTORY: ProductHistoryState = { loading: false, error: null, points: [], summary: null };
const EMPTY_CROSS: ProductCrossState = { loading: false, error: null, items: [], note: '' };
const EMPTY_DETAIL: ProductDetailState = { loading: false, error: null, data: null };

/** ⚠️ 모듈 상수 — 갈래마다 새 배열을 만들면 소비처의 `useMemo` 가 매 렌더 다시 돈다 */
const EMPTY_POINTS: ProductRankingHistoryPoint[] = [];
const EMPTY_MATCHES: ProductCrossChannelMatch[] = [];

function messageOf(caught: unknown, fallback: string): string {
  const failure = caught as { message?: unknown };
  return typeof failure?.message === 'string' && failure.message ? failure.message : fallback;
}

/**
 * 수집 직후 **다시 읽는 간격**(ms). 0 은 곧바로 한 번.
 *
 * ⚠️ **한 번만 읽으면 「상세 없음」을 그대로 다시 읽는다.** 수집 완료 신호와 그 결과가
 * ES 에서 보이기 시작하는 시점 사이에 틈이 있어(색인 refresh), 이벤트를 받자마자 물어보면
 * 방금 쓴 문서가 아직 안 잡힌다. 그 「없음」이 앞단 `runCached`(TTL 이 없다)에 그대로
 * 굳어 버려서, 화면을 나갔다 다시 들어와 **재수집이 한 번 더 돌기 전까지** 계속 안 뜬다 —
 * 「수집은 끝났는데 화면에는 수집 버튼이 그대로」의 정체가 이것이다.
 *
 * 그래서 **상세가 실제로 보일 때까지** 몇 번 더 물어본다. 사다리를 다 쓰고도 없으면
 * 정말로 없는 것으로 보고 그만둔다(그때는 앞단 캐시를 비워 다음 사람이 다시 물어볼 수 있게 둔다).
 */
const RELOAD_DELAYS_MS = [0, 1000, 2500, 5000];

const wait = (ms: number) => new Promise<void>((resolve) => { setTimeout(resolve, ms); });

/**
 * 상품 하나의 **이력·타채널·상세**를 한꺼번에 로드한다 (`active`일 때만).
 *
 * ## ⚠️ 세대 번호는 따로 두지 않는다
 *
 * **세 쿼리의 키가 모두 «상품 좌표»** 라서 상품을 갈아타면 세 키가 함께 바뀌고, 늦게 온
 * 응답은 자기 키의 캐시로 들어가 이 상품 화면에는 안 뜬다 — 한 칸만 다른 상품인 화면이
 * 안 생긴다. 좌표를 키에서 빼면 그 보장이 사라진다.
 *
 * ## ⚠️ 랭킹 밖 상품은 셋 중 하나만 부른다
 *
 * 판매몰 실시간 검색분(`ranked === false`)은 순위·타채널 데이터가 없다. 다만 **상세는
 * 부른다** — 검색 스냅샷(`service: "search"`)에 상세 수집분이 upsert되기 때문이다.
 *
 * ## ⚠️ 이력과 타채널은 좌표가 다르다
 *
 * 이력은 상세와 **같은 좌표**(`kind` 포함)를 써야 한다 — 다르면 다른 상품을 본다.
 * 타채널은 반대로 **`kind`를 안 넘긴다** — 다른 채널의 같은 상품을 찾는 질문이라
 * 이쪽 종류로 저쪽을 가리면 안 된다.
 *
 * ## ⚠️ 좌표를 못 만드는 상품에서 이력은 「조회 중」에 머문다
 *
 * 좌표(`ref`)를 못 만들면 이력은 `loading:true` 인 채로 멈춘다 — 그 갈래는 스피너가 안
 * 풀린다. 일부러 그대로 둔다.
 *
 * ⚠️ `keepPreviousData` 를 **안 쓴다** — 상품을 갈아타면 셋을 비운다.
 * ⚠️ `staleTime` 60초 — ES 질문이라 같은 좌표면 같은 결과다. 앞단 `runCached` 가 그대로라
 * 캐시가 이중이고, 상세 수집 직후의 무효화 경로는 여전히 `invalidateCache` 쪽이다.
 */
/** ⚠️ 참조를 고정한다 — 매번 새 배열이면 아래 메모가 헛돈다(다른 EMPTY_* 와 같은 규약) */
const EMPTY_GALLERY: string[] = [];

/** 비교 테이블의 고시정보 한 줄 — 서버 통합본과 같은 모양이다 */
/** 고시정보 한 줄 — 유선 모양은 경계(`actions/products/product-bff`)가 쥔다 */
export type { ProductNoticeItem };
const EMPTY_NOTICE: ProductNoticeItem[] = [];

export function useProductDetail(product: MarketProduct | null | undefined, active: boolean) {
  const queryClient = useQueryClient();

  /** ⚠️ 사다리가 도는 중에 카드가 사라질 수 있다 — 언마운트 뒤에는 더 묻지 않는다 */
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const postId = product?.postId;
  const source = product?.source;
  const kind = product?.kind;
  const brand = product?.brand;
  const title = product?.title;
  const rankable = product?.ranked !== false;
  const fetchable = Boolean(active && postId);

  const detailRef = useMemo(() => detailRefFor({ postId, source, kind }), [postId, source, kind]);
  const detailKey = useMemo(
    () => (detailRef ? cacheKey('rankings.detail', detailRef) : ''),
    [detailRef],
  );
  const historyKey = useMemo(
    () => (detailRef ? cacheKey('rankings.history', detailRef) : ''),
    [detailRef],
  );
  const crossParams = useMemo(
    () => ({ postId: postId || '', source: source || '', brand: brand || '', title: title || '' }),
    [postId, source, brand, title],
  );
  const crossKey = useMemo(() => cacheKey('rankings.crossChannel', crossParams), [crossParams]);

  const detailQuery = useQuery({
    queryKey: marketKeys.detail(detailKey),
    queryFn: () => runCached(detailKey, () => fetchProductRankingDetail(detailRef!)),
    enabled: fetchable && Boolean(detailRef),
    staleTime: 60_000,
  });

  const historyQuery = useQuery({
    queryKey: marketKeys.history(historyKey),
    queryFn: () => runCached(historyKey, () => fetchProductRankingHistory(detailRef!)),
    enabled: fetchable && rankable && Boolean(detailRef),
    staleTime: 60_000,
  });

  /**
   * 아마존 **대표 컷** — 색인에 사진이 없을 때만 단건으로 받는다.
   *
   * ⚠️ 검색 응답에는 썸네일이 한 장뿐이라(수집 담당 확인) 랭킹에 아직 안 들어온 상품은
   * 비교 테이블의 「대표이미지」 줄이 한 장으로 선다. 비교에 담은 상품만 여기서 채운다 —
   * 목록 단계에서 전부 받으면 상품마다 외부를 한 번씩 더 치게 된다.
   */
  const galleryAsin = useMemo(() => {
    if (String(source || '').toLowerCase() !== 'amazon') return '';
    if (Array.isArray(product?.images) && product.images.length > 1) return '';
    return String(product?.liveId || postId || '').trim().toUpperCase();
  }, [source, product?.images, product?.liveId, postId]);

  const galleryQuery = useQuery({
    queryKey: marketKeys.detail(`amazon-detail:${galleryAsin}`),
    queryFn: () => fetchAmazonProductGallery(galleryAsin),
    enabled: Boolean(active && galleryAsin),
    staleTime: 300_000,
  });

  const crossQuery = useQuery({
    queryKey: marketKeys.crossChannel(crossKey),
    queryFn: () => runCached(crossKey, () => fetchProductCrossChannel(crossParams)),
    enabled: fetchable && rankable,
    staleTime: 60_000,
  });

  const detail = useMemo<ProductDetailState>(() => {
    if (!fetchable || !detailRef) return EMPTY_DETAIL;
    if (detailQuery.isError) {
      return {
        loading: false,
        error: messageOf(detailQuery.error, '상세 정보를 불러오지 못했습니다.'),
        data: null,
      };
    }
    if (!detailQuery.data) return { loading: true, error: null, data: null };
    return { loading: detailQuery.isFetching, error: null, data: detailQuery.data };
  }, [fetchable, detailRef, detailQuery.isError, detailQuery.error, detailQuery.data, detailQuery.isFetching]);

  const history = useMemo<ProductHistoryState>(() => {
    if (!fetchable || !rankable) return EMPTY_HISTORY;
    // ⚠️ 좌표를 못 만들면 loading:true 인 채로 멈춘다 — 일부러 그대로 둔다(머리말 참조)
    if (!detailRef) return { ...EMPTY_HISTORY, loading: true };
    if (historyQuery.isError) {
      return {
        loading: false,
        error: messageOf(historyQuery.error, '이력을 불러오지 못했습니다.'),
        points: EMPTY_POINTS,
        summary: null,
      };
    }
    if (!historyQuery.data) return { ...EMPTY_HISTORY, loading: true };
    return {
      loading: false,
      error: null,
      points: historyQuery.data.points || EMPTY_POINTS,
      summary: historyQuery.data.summary || null,
    };
  }, [fetchable, rankable, detailRef, historyQuery.isError, historyQuery.error, historyQuery.data]);

  const cross = useMemo<ProductCrossState>(() => {
    if (!fetchable || !rankable) return EMPTY_CROSS;
    if (crossQuery.isError) {
      return {
        loading: false,
        error: messageOf(crossQuery.error, '타 채널 정보를 불러오지 못했습니다.'),
        items: EMPTY_MATCHES,
        note: '',
      };
    }
    if (!crossQuery.data) return { ...EMPTY_CROSS, loading: true };
    return {
      loading: false,
      error: null,
      items: crossQuery.data.items || EMPTY_MATCHES,
      // ⚠️ `note`는 건너뛴 갈래에만 있다
      note: crossQuery.data.matchedBy === 'none' ? crossQuery.data.note || '' : '',
    };
  }, [fetchable, rankable, crossQuery.isError, crossQuery.error, crossQuery.data]);

  /**
   * 상세 수집 직후처럼, 캐시를 버리고 다시 읽어야 할 때.
   *
   * ⚠️ 앞단 `runCached` 와 RQ **둘 다** 버려야 한다 — 하나만 버리면 다른 하나가 옛 값을 돌려준다.
   *
   * ⚠️ **한 번으로 끝내지 않는다** — 상세가 잡힐 때까지 `RELOAD_DELAYS_MS` 사다리를 탄다.
   * 이유는 그 상수의 머리말에 있다.
   *
   * ⚠️ 조회를 끈 상태(`fetchable` 이 아니다)면 사다리를 타지 않는다. RQ 가 꺼진 쿼리는
   * 무효화해도 다시 읽지 않아 몇 초 동안 헛돌기만 한다 — 앞단 캐시만 버리고 빠진다.
   * 다시 활성화되는 순간(카드가 화면에 들어올 때) 어차피 새로 읽는다.
   */
  const reloadDetail = useCallback(async () => {
    if (!detailKey) return;
    if (!fetchable) {
      invalidateCache(detailKey);
      return;
    }
    const queryKey = marketKeys.detail(detailKey);
    for (let attempt = 0; attempt < RELOAD_DELAYS_MS.length; attempt += 1) {
      const delay = RELOAD_DELAYS_MS[attempt];
      if (delay) await wait(delay);
      if (!aliveRef.current) return;
      invalidateCache(detailKey);
      await queryClient.invalidateQueries({ queryKey });
      if (!aliveRef.current) return;
      if (hasCollectedDetail(queryClient.getQueryData(queryKey))) return;
    }
    // 끝까지 「없음」이면 그 답을 앞단 캐시에 남기지 않는다 — 다음에 읽는 쪽이 다시 물어볼 수 있게
    invalidateCache(detailKey);
  }, [detailKey, fetchable, queryClient]);

  /**
   * 아마존 단건에서 온 것 — **색인 문서가 없을 때만** 채워진다.
   *
   * ⚠️ 상세 수집(A+ 이미지)과 다른 경로다. 이쪽은 상품 페이지 한 번 읽어 오는 것이라
   * 버튼을 안 눌러도 대표 컷과 고시정보(전성분·사용방법)가 바로 온다(수집 담당 확인).
   */
  const gallery = useMemo<string[]>(
    () => galleryQuery.data?.images ?? EMPTY_GALLERY,
    [galleryQuery.data],
  );

  const liveNotice = useMemo<ProductNoticeItem[]>(
    () => galleryQuery.data?.notice ?? EMPTY_NOTICE,
    [galleryQuery.data],
  );

  return { history, cross, detail, gallery, liveNotice, reloadDetail };
}
